/**
 * The bone map between the animation rig and the hero rig, and the layer regions
 * derived from it.
 *
 * Three bone-naming conventions appear across the art collection. Two matter for the
 * player character:
 *
 * - **Rig A** -- "Synty animation rig", 52 bones. Every one of the 1,436 animation
 *   clips is authored on it. Bones point down +Y.
 * - **Rig C** -- "Synty generic rig", 49 core bones. What `POLYGON_ModularFantasyHero`
 *   is skinned to. Bones point down +X (-X for legs).
 *
 * Measured during M1, and stronger than §6.1 of the asset document claimed: the two
 * rigs place every shared bone at the **same world position**, agreeing to 6.7e-5 cm.
 * They are one skeleton in two orientation conventions, so the retarget is a rename
 * plus a constant per-bone change of basis, with no positional correction at all.
 *
 * The rows live in `rigMap.json` rather than in this file because the asset pipeline
 * reads the same data to retarget the clips. One source of truth, validated against
 * `art-source/` by `npm run pipeline -- check`.
 *
 * The import attribute on that import is **required**, not decoration. A bundler infers
 * the type from the extension, so Vite is happy without it -- but anything that reaches
 * this module through plain Node ESM throws `needs an import attribute of "type: json"`
 * before a single line runs, and it throws while the module graph is being built, so the
 * caller sees the whole graph fail rather than one import. That is not hypothetical: it
 * took the entire Playwright suite down the first time a spec imported the barrel for a
 * value, because the barrel re-exports this file.
 *
 * Rig B (the UE4 mannequin used by the `Unreal_Characters/` enemies) is deliberately
 * absent: it is needed for enemy retargeting, not for the player, and its finger
 * topology differs enough that mapping it needs its own measurement pass.
 *
 * See develop/ExploreDungeonAssets.md §6.2 and §9.2.
 */
import rigMap from './rigMap.json' with { type: 'json' };

/** Bone naming convention. The runtime skeleton is rig C. */
export const CANONICAL_RIG = 'rigC';

/**
 * Which animation layer a bone belongs to.
 *
 * `root` is driven by neither layer: the battle server owns position, so clips play
 * in place and nothing animates the root (§8.2).
 */
export type BoneRegion = 'root' | 'lower' | 'upper';

export interface BoneMapRow {
  /** Name in the animation clips. */
  rigA: string;
  /** Name on the hero skeleton, or null where rig C has no equivalent. */
  rigC: string | null;
  region: BoneRegion;
}

export const BONE_MAP: readonly BoneMapRow[] = rigMap.rows as readonly BoneMapRow[];

/**
 * Names the pipeline synthesises onto the hero rig, absent from the shipped art.
 *
 * `Prop_L`/`Prop_R` are the weapon bones the animators posed against. They exist on
 * rig A only, so the pipeline measures their rig A world transform and re-expresses
 * it against the hero's hand -- taking the world transform, not the local offset,
 * because the two rigs orient the hand differently.
 */
export const SYNTHESISED_BONES = {
  propLeft: 'Prop_L',
  propRight: 'Prop_R',
  /**
   * The quiver hangs here. Rig A has no attachment bones at all, and placement is not
   * guesswork: step 6.5 samples `Hand_R` during a `Reload` clip and moves the node to
   * where the animator reached. Until then it is parented to the upper spine at a
   * provisional offset so nothing has to be re-rigged later.
   */
  quiver: 'Quiver',
} as const;

/**
 * Rig A -> rig C lookup. Bones with no rig C equivalent are omitted, so a miss means
 * "drop this bone's keyframes" rather than "unknown bone".
 */
export const RIG_A_TO_C: ReadonlyMap<string, string> = new Map(
  BONE_MAP.flatMap((row) => (row.rigC === null ? [] : [[row.rigA, row.rigC] as const])),
);

/** Canonical (rig C) bone names in a layer region, in skeleton order. */
export function canonicalBonesInRegion(region: BoneRegion): string[] {
  return BONE_MAP.flatMap((row) => (row.region === region && row.rigC !== null ? [row.rigC] : []));
}

/** Empty when the bone map is internally consistent. Run by `pipeline check`. */
export function verifyBoneMap(): string[] {
  const problems: string[] = [];

  // Rig A ships 52 bones, confirmed against the canonical sample file. 51 of them
  // have a rig C target: the 49 core hero bones plus the two synthesised Prop bones.
  // `Jaw` is the one drop -- rig C has no jaw, and at this art style's scale that is
  // invisible.
  if (BONE_MAP.length !== 52) {
    problems.push(`expected 52 rig A bones, found ${BONE_MAP.length}`);
  }
  const targeted = BONE_MAP.filter((row) => row.rigC !== null);
  if (targeted.length !== 51) {
    problems.push(`expected 51 mapped rig C bones, found ${targeted.length}`);
  }

  for (const [rig, names] of [
    ['rigA', BONE_MAP.map((row) => row.rigA)],
    ['rigC', targeted.map((row) => row.rigC as string)],
  ] as const) {
    const seen = new Set<string>();
    for (const name of names) {
      if (seen.has(name)) problems.push(`${rig}: duplicate bone "${name}"`);
      seen.add(name);
    }
  }

  // The seam only works if every bone is claimed exactly once, and the root by
  // nothing. `canonicalBonesInRegion` is the sole source of the layer masks, so a
  // gap here becomes a bone that no layer drives.
  const roots = BONE_MAP.filter((row) => row.region === 'root');
  if (roots.length !== 1 || roots[0]?.rigA !== 'Root') {
    problems.push('expected exactly one bone in the `root` region, named Root');
  }

  return problems;
}
